import { useState, useEffect, cloneElement } from 'react'
import * as React from 'react'
import Styles from './CommentForm.module.scss'
import cx from 'classnames'
import qs from 'qs'
import { CommentFormProps, CommentFormSubmitPayload } from './Types'
import { UserAvatar, UserAvatarUser } from 'components/Comment/UserAvatar'
import { CommentInput } from 'components/CommentInput/CommentInput'
import { RootState } from 'types'
import { useSelector, useDispatch } from 'react-redux'
import isEqual from 'lodash/isEqual'
import { renameAnonymousUser } from 'redux/actions/anonymousUser'
import { reportError } from 'utils/error'
import useIsMounted from 'ismounted'
import { useObjectRef } from '@react-aria/utils'
import Collapse from 'react-css-collapse'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { useKeyboard } from '@react-aria/interactions'
import { ProseInputProps } from 'common/ProseInput/Types'
import { ProseState } from 'common/ProseInput/ProseState'
import { ProseStateProvider } from 'common/ProseInput/ProseStateContext'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useLocation } from 'react-router-dom'
import { Button } from 'blaze-ui/Button'

export interface CTAProps {
  onPress: () => void
  isDisabled: boolean
  cta?: React.ReactElement
  preventFocusOnPress?: boolean
}

const PrimaryCta = ({ onPress, isDisabled, preventFocusOnPress, cta }: CTAProps) => {
  const ctaProps = {
    onPress: onPress,
    isDisabled: isDisabled,
    preventFocusOnPress: preventFocusOnPress,
  }

  if (cta) {
    return cloneElement(cta, ctaProps)
  }

  return (
    <Button {...ctaProps} size="sm">
      Post
    </Button>
  )
}
const SecondaryCta = ({ isDisabled, cta, onPress }: CTAProps) => {
  const ctaProps = {
    onPress: onPress,
    isDisabled: isDisabled,
  }

  if (cta) {
    return cloneElement(cta, ctaProps)
  }

  return (
    <Button variant="tertiary" {...ctaProps} size="sm">
      Cancel
    </Button>
  )
}

const CommentForm = ({
  autoFocus = false,
  value,
  onChange,
  onSubmit,
  disableEmptyCancel = false,
  onCancel,
  placeholder = 'Write a reply...',
  onFocus,
  onBlur,
  className,
  inputClassName,
  inputContainerClassName,
  inputRef,
  primaryCta,
  secondaryCta,
  showAvatar = true,
  hideActionsOnEmpty = false,
  isHtmlComment = false,
  footer,
}: CommentFormProps) => {
  const { auth, anonymousUser, unregisteredUser } = useSelector(
    (state: RootState) => ({
      auth: state.auth,
      anonymousUser: state.anonymousUser,
      unregisteredUser: state.unregisteredUser,
    }),
    isEqual,
  )
  const dispatch = useDispatch()
  const location = useLocation()
  const params = qs.parse(location.search, { ignoreQueryPrefix: true })
  const { userId } = params
  const unauthUser = !auth && !!userId
  const authedUser = auth ?? unregisteredUser.user
  const user = authedUser
    ? { ...authedUser, type: 'User' }
    : ({ ...anonymousUser, type: 'AnonymousUser' } as UserAvatarUser)
  const anonymousUserDisplayName = !unauthUser && anonymousUser?.display_name
  const [customDisplayName, setCustomDisplayName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const isMounted = useIsMounted()
  const canEditName = anonymousUser && !unauthUser
  const ref = useObjectRef(inputRef as React.RefObject<HTMLTextAreaElement>)
  const { isMobile } = useResponsiveDesign()

  useEffect(() => {
    if (autoFocus) {
      const timeout = setTimeout(() => {
        const length = value.length
        ref.current?.focus()
        ref.current?.setSelectionRange?.(length, length)
      }, 50)
      return () => clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus])

  const handleFocus = () => {
    onFocus?.()
  }

  const handleBlur = () => {
    onBlur?.()
  }

  const handleCancel = () => {
    onCancel?.()
  }

  useEffect(() => {
    if (anonymousUserDisplayName && customDisplayName === '' && anonymousUserDisplayName !== 'Anonymous User') {
      setCustomDisplayName(anonymousUserDisplayName)
    }
  }, [anonymousUserDisplayName, customDisplayName])

  const handleSubmit = () => {
    if (submitting || value === '') {
      return
    }
    setSubmitting(true)
    let payload: CommentFormSubmitPayload = { richText: isHtmlComment }

    if (customDisplayName !== '') {
      dispatch(renameAnonymousUser(customDisplayName))
      payload = { ...payload, anonymousUserDisplayName: customDisplayName }
    }

    if (unauthUser) {
      payload = { ...payload, userId }
    }

    onSubmit(payload)
      .catch((e) => reportError(e))
      .finally(() => {
        if (!isMounted.current) {
          return
        }
        setSubmitting(false)
      })
  }

  const showActions = value !== ''

  /* -------------------------------------------------------------------------- */
  /*                              Keyboard handlers                             */
  /* -------------------------------------------------------------------------- */

  /**
   * Form handlers: these handlers only trigger behaviours outside of the editor
   * itself, like submitting on enter or blurring on escape. They DO NOT
   * interfere with the editing experience. Editor specific handlers are
   * implemented below.
   */
  const { keyboardProps: formKeyboardHandlers } = useKeyboard({
    onKeyDown: ({ continuePropagation, key, shiftKey }) => {
      if (key === 'Escape') {
        handleCancel()
        continuePropagation()
      }

      if (key === 'Enter' && !shiftKey && isHtmlComment && !isMobile) {
        handleSubmit()
      }
    },
  })

  /**
   * Editor specific handlers: these handlers are passed down to the prosemirror
   * instance and dictate whether the event should stop or keep going through
   * the chain of handlers attached to the editor.
   * In this case, we return true to stop the editor from inserting a new line,
   * right before submitting the form.
   *
   * See more info here:
   * https://prosemirror.net/docs/ref/#view.EditorProps.handleKeyDown
   */
  const editorHandleKeyDown: ProseInputProps['handleKeyDown'] = (_, { key, shiftKey }) => {
    if (key === 'Enter' && !shiftKey && !isMobile) {
      return true
    }
    return false
  }

  const [proseState] = useState(new ProseState())

  return (
    <ProseStateProvider state={proseState}>
      <div className={className}>
        <div className={inputContainerClassName} {...formKeyboardHandlers}>
          {showAvatar && <UserAvatar user={user} size={24} />}
          <div className={Styles.content}>
            {canEditName && (
              <input
                className={Styles.displayNameInput}
                type="text"
                placeholder="Type your name here"
                value={customDisplayName}
                onChange={(e) => setCustomDisplayName(e.target.value)}
              />
            )}
            <CommentInput
              auth={auth}
              className={cx(Styles.input, inputClassName, { [Styles.richText]: isHtmlComment })}
              inputRef={ref}
              placeholder={placeholder}
              onFocus={handleFocus}
              onBlur={handleBlur}
              value={value}
              onChange={onChange}
              tabIndex={0}
              isHtmlComment={isHtmlComment}
              handleKeyDown={editorHandleKeyDown}
              deprecatedOnEnter={(e) => {
                e.preventDefault()
                handleSubmit()
              }}
            />
          </div>
        </div>
        <ConditionalWrapper
          condition={hideActionsOnEmpty}
          wrapper={(children) => <Collapse isOpen={showActions}>{children}</Collapse>}
        >
          <div className={Styles.footer}>
            {footer}
            <div className={Styles.actions}>
              {onCancel && (
                <SecondaryCta
                  onPress={onCancel}
                  cta={secondaryCta}
                  isDisabled={(value.trim() === '' && disableEmptyCancel) || submitting}
                />
              )}
              <PrimaryCta
                onPress={handleSubmit}
                isDisabled={value.trim() === '' || submitting}
                cta={primaryCta}
                preventFocusOnPress={isMobile}
              />
            </div>
          </div>
        </ConditionalWrapper>
      </div>
    </ProseStateProvider>
  )
}

export { CommentForm }
